import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Card,
  Button,
  Descriptions,
  Space,
  Tag,
  Divider,
  Spin,
  Alert,
  Timeline,
  Popconfirm,
  Tooltip,
  message,
} from 'antd';
import {
  LeftOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  StarOutlined,
  InfoCircleOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import { useStores } from '../../stores';
import orderService from '../../services/orderService';
import ReviewForm from '../../components/reviews/ReviewForm';
import './OrderDetailPage.scss';

const { Title, Text } = Typography;

const OrderDetailPage = observer(() => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { cartStore } = useStores();
  const [cancelling, setCancelling] = useState(false);
  const [paying, setPaying] = useState(false);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedItemForReview, setSelectedItemForReview] = useState(null);
  const [reviewedItems, setReviewedItems] = useState(new Set());

  const openReviewModal = (item) => {
    setSelectedItemForReview(item);
    setReviewModalVisible(true);
  };

  const handleReviewSuccess = () => {
    // Mark this item as reviewed locally
    if (selectedItemForReview) {
      setReviewedItems(prev => new Set([...prev, selectedItemForReview.id]));
    }
    // Refresh order to get updated review status
    cartStore.fetchOrderById(id);
  };

  useEffect(() => {
    cartStore.fetchOrderById(id);

    return () => {
      cartStore.clearCurrentOrder();
    };
  }, [id, cartStore]);

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      CONFIRMED: 'blue',
      PROCESSING: 'cyan',
      SHIPPED: 'purple',
      DELIVERED: 'green',
      CANCELLED: 'red',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      PENDING: t('orders.status.pendingPayment'),
      CONFIRMED: t('orders.status.confirmed'),
      PROCESSING: t('orders.status.processing'),
      SHIPPED: t('orders.status.shipped'),
      DELIVERED: t('orders.status.delivered'),
      CANCELLED: t('orders.status.cancelled'),
    };
    return texts[status] || status;
  };

  const getTimelineItems = (order) => {
    const items = [];

    items.push({
      color: 'green',
      dot: <CheckCircleOutlined />,
      children: (
        <div>
          <Text strong>{t('orders.detail.timeline.placed')}</Text>
          <br />
          <Text type="secondary">
            {new Date(order.created_at).toLocaleString()}
          </Text>
        </div>
      ),
    });

    if (order.confirmed_at) {
      items.push({
        color: 'blue',
        dot: <CheckCircleOutlined />,
        children: (
          <div>
            <Text strong>{t('orders.detail.timeline.confirmed')}</Text>
            <br />
            <Text type="secondary">
              {new Date(order.confirmed_at).toLocaleString()}
            </Text>
          </div>
        ),
      });
    }

    if (order.shipped_at) {
      items.push({
        color: 'purple',
        dot: <CheckCircleOutlined />,
        children: (
          <div>
            <Text strong>{t('orders.detail.timeline.shipped')}</Text>
            <br />
            <Text type="secondary">
              {new Date(order.shipped_at).toLocaleString()}
            </Text>
          </div>
        ),
      });
    }

    if (order.delivered_at) {
      items.push({
        color: 'green',
        dot: <CheckCircleOutlined />,
        children: (
          <div>
            <Text strong>{t('orders.detail.timeline.delivered')}</Text>
            <br />
            <Text type="secondary">
              {new Date(order.delivered_at).toLocaleString()}
            </Text>
          </div>
        ),
      });
    }

    if (order.cancelled_at) {
      items.push({
        color: 'red',
        dot: <CloseCircleOutlined />,
        children: (
          <div>
            <Text strong>{t('orders.detail.timeline.cancelled')}</Text>
            <br />
            <Text type="secondary">
              {new Date(order.cancelled_at).toLocaleString()}
            </Text>
          </div>
        ),
      });
    }

    return items;
  };

  const handleCancelOrder = async () => {
    setCancelling(true);
    const result = await cartStore.cancelOrder(id);
    setCancelling(false);

    if (result.success) {
      message.success(t('orders.cancelSuccess'));
    } else {
      message.error(result.error || t('orders.cancelFailed'));
    }
  };

  const handlePayNow = async () => {
    setPaying(true);
    const result = await orderService.createPaymentSession(id);

    if (result.success) {
      window.location.href = result.data.checkout_url;
    } else {
      message.error(result.error || t('orders.paymentFailed'));
      setPaying(false);
    }
  };

  if (cartStore.ordersLoading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip={t('orders.detail.loading')} />
      </div>
    );
  }

  if (cartStore.error || !cartStore.currentOrder) {
    return (
      <div className="sweet-container" style={{ padding: '48px 24px' }}>
        <Alert
          message={t('common.error')}
          description={cartStore.error || t('orders.detail.notFound')}
          type="error"
          showIcon
        />
        <Button
          icon={<LeftOutlined />}
          onClick={() => navigate('/orders')}
          style={{ marginTop: 16 }}
        >
          {t('orders.detail.backToOrders')}
        </Button>
      </div>
    );
  }

  const order = cartStore.currentOrder;

  return (
    <div className="order-detail-page">
      <div className="sweet-container">
        <Button
          icon={<LeftOutlined />}
          onClick={() => navigate('/orders')}
          className="back-button"
        >
          {t('orders.detail.backToOrders')}
        </Button>

        <div className="order-header">
          <div>
            <Title level={2}>{t('orders.detail.title')}</Title>
            <Text type="secondary">{t('orders.detail.orderNumber')} {order.order_number}</Text>
          </div>
          <Tag color={getStatusColor(order.status)} className="status-tag">
            {getStatusText(order.status)}
          </Tag>
        </div>

        <Divider />

        <Card className="order-info-card">
          <Descriptions column={{ xs: 1, sm: 2 }} bordered>
            <Descriptions.Item label={t('orders.detail.orderNumber')}>
              <Text strong>{order.order_number}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('orders.detail.orderDate')}>
              {new Date(order.created_at).toLocaleDateString()}
            </Descriptions.Item>
            <Descriptions.Item label={t('orders.detail.partner')}>
              <Space>
                <EnvironmentOutlined />
                {order.partner_name}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.status')}>
              <Tag color={getStatusColor(order.status)}>
                {getStatusText(order.status)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('orders.detail.shippingAddress')} span={2}>
              {order.shipping_address}
            </Descriptions.Item>
            {order.notes && (
              <Descriptions.Item label={t('common.notes')} span={2}>
                {order.notes}
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* Payment Required Alert for PENDING orders */}
          {order.status === 'PENDING' && (
            <div style={{ marginTop: 24 }}>
              <Alert
                message={t('orderConfirmation.paymentRequired')}
                description={t('orderConfirmation.paymentRequiredDesc')}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Button
                type="primary"
                size="large"
                icon={<CreditCardOutlined />}
                onClick={handlePayNow}
                loading={paying}
              >
                {t('orders.payNow')}
              </Button>
            </div>
          )}

          {order.can_cancel && (
            <div style={{ marginTop: 24 }}>
              <Alert
                message={t('orders.detail.cancellationPolicy.title')}
                description={t('orders.detail.cancellationPolicy.description')}
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
              />
              <Popconfirm
                title={t('orders.cancelOrder')}
                description={t('orders.cancelConfirm')}
                onConfirm={handleCancelOrder}
                okText={t('orders.detail.yesCancelOrder')}
                cancelText={t('common.no')}
                okButtonProps={{ danger: true }}
              >
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  loading={cancelling}
                >
                  {t('orders.cancelOrder')}
                </Button>
              </Popconfirm>
            </div>
          )}
        </Card>

        <Card className="order-timeline-card" title={t('orders.detail.timeline.title')}>
          <Timeline items={getTimelineItems(order)} />
        </Card>

        <Card className="order-items-card" title={t('orders.detail.orderItems')}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {order.items.map((item) => {
              const isReviewed = item.is_reviewed || reviewedItems.has(item.id);
              const canReview = order.status === 'DELIVERED' && !isReviewed;

              return (
                <Card key={item.id} type="inner" size="small">
                  <div className="order-item">
                    <div style={{ flex: 1 }}>
                      <Text strong>{item.product_name}</Text>
                      <br />
                      <Text type="secondary">{item.variant_details}</Text>
                      <br />
                      <Text type="secondary">
                        €{parseFloat(item.unit_price).toFixed(2)} × {item.quantity}
                      </Text>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Text strong style={{ fontSize: 16 }}>
                        €{parseFloat(item.subtotal).toFixed(2)}
                      </Text>
                      {canReview && (
                        <div style={{ marginTop: 8 }}>
                          <Button
                            type="primary"
                            size="small"
                            icon={<StarOutlined />}
                            onClick={() => openReviewModal(item)}
                          >
                            {t('orders.detail.rateProduct')}
                          </Button>
                        </div>
                      )}
                      {isReviewed && (
                        <div style={{ marginTop: 8 }}>
                          <Tag color="green" icon={<CheckCircleOutlined />}>
                            {t('orders.detail.reviewed')}
                          </Tag>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </Space>

          <Divider />

          <div className="order-totals">
            <div className="total-row">
              <Text type="secondary">{t('common.subtotal')}:</Text>
              <Text>€{parseFloat(order.subtotal).toFixed(2)}</Text>
            </div>
            <div className="total-row">
              <Text type="secondary">{t('checkout.shipping')}:</Text>
              <Text>€{parseFloat(order.shipping_cost).toFixed(2)}</Text>
            </div>
            <div className="total-row">
              <Text type="secondary">{t('orders.detail.platformFee')}</Text>
              <Text>€{parseFloat(order.platform_fee).toFixed(2)}</Text>
            </div>
            <Divider />
            <div className="total-row">
              <Title level={4} style={{ margin: 0 }}>{t('common.total')}:</Title>
              <Title level={4} style={{ margin: 0, color: '#FF9A76' }}>
                €{parseFloat(order.total_amount).toFixed(2)}
              </Title>
            </div>
          </div>
        </Card>

        {/* Review Modal */}
        {selectedItemForReview && (
          <ReviewForm
            visible={reviewModalVisible}
            onClose={() => {
              setReviewModalVisible(false);
              setSelectedItemForReview(null);
            }}
            onSuccess={handleReviewSuccess}
            type="product"
            itemId={selectedItemForReview.product_id}
            referenceId={selectedItemForReview.id}
            itemName={selectedItemForReview.product_name}
          />
        )}
      </div>
    </div>
  );
});

export default OrderDetailPage;
